import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Pencil, Search, X, Loader2 } from 'lucide-react';
import {
  fetchLeaveTypes,
  createLeaveType,
  updateLeaveType,
  setSearch,
  clearError,
  selectLeaveTypes,
  selectLeaveTypesLoading,
  selectLeaveTypesError,
  selectLeaveTypesSaving,
  selectLeaveTypesSaveError,
  selectLeaveTypesSearch,
} from '../../store/slices/leaveTypesSlice';

const FIELD =
  'h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-xs text-gray-700 outline-none focus:border-primary-500';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';

const EMPTY_FORM = {
  code: '',
  name: '',
  paid: 'Yes',
  status: 'Active',
};

export default function LeaveTypes() {
  const dispatch = useDispatch();

  // Redux state
  const types = useSelector(selectLeaveTypes);
  const loading = useSelector(selectLeaveTypesLoading);
  const error = useSelector(selectLeaveTypesError);
  const saving = useSelector(selectLeaveTypesSaving);
  const saveError = useSelector(selectLeaveTypesSaveError);
  const search = useSelector(selectLeaveTypesSearch);

  // Local state for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    dispatch(fetchLeaveTypes());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return types;
    return types.filter((t) =>
      (t.code || '').toLowerCase().includes(q) ||
      (t.name || '').toLowerCase().includes(q)
    );
  }, [types, search]);

  function openAddModal() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEditModal(type) {
    setEditingId(type.id);
    setForm({
      code: type.code,
      name: type.name,
      paid: type.paid,
      status: type.status
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    dispatch(clearError());
  }

  async function handleSave() {
    if (!form.code.trim() || !form.name.trim()) return;

    try {
      const maxDisplayOrder = types.reduce(
        (max, type) => Math.max(max, Number(type.displayOrder) || 0),
        0
      );

      const payload = {
        typeCode: form.code.trim().toUpperCase(),
        typeName: form.name.trim(),
        isActive: form.status === 'Active' ? 'A' : 'I',
        displayOrder: maxDisplayOrder + 1,
        typeIsPaid: form.paid === 'Yes' ? 'Y' : 'N',
        userId: 'SYSTEMSEMS',
      };

      if (editingId) {
        payload.typeId = editingId;
        await dispatch(updateLeaveType(payload)).unwrap();
      } else {
        await dispatch(createLeaveType(payload)).unwrap();
      }

      await dispatch(fetchLeaveTypes()).unwrap();
      closeModal();
    } catch (error) {
      console.error('Save failed:', error);
    }
  }

  const displayError = error || saveError;

  // ─── Table Columns ──────────────────────────────────────────────────
  const leaveTypeColumns = [
    {
      key: 'code',
      label: 'Code',
      width: '120px',
      render: (r) => <span className="text-gray-600">{r.code || '—'}</span>,
    },
    {
      key: 'name',
      label: 'Leave Name',
      width: '240px',
      render: (r) => (
        <span className="font-semibold text-gray-900">{r.name || '—'}</span>
      ),
    },
    {
      key: 'paid',
      label: 'Paid',
      width: '120px',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.paid === 'Yes' ? 'bg-emerald-500' : 'bg-gray-400'}`}
          />
          <span className="text-gray-600">{r.paid}</span>
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.status === 'Active' ? 'bg-emerald-500' : 'bg-gray-400'}`}
          />
          <span className="text-gray-600">{r.status}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '90px',
      align: 'center',
      render: (r) => (
        <button
          onClick={() => openEditModal(r)}
          title="Edit"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
        >
          <Pencil size={13} className="flex-shrink-0" />
        </button>
      ),
    },
  ];

  const alignClass = (align) =>
    align === 'center'
      ? 'text-center'
      : align === 'right'
        ? 'text-right'
        : 'text-left';

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters (Search + Add Button) ────────────────────────── */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <span className={LABEL}>Search</span>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => dispatch(setSearch(e.target.value))}
              placeholder="Search by code or name..."
              className={`${FIELD} max-w-[400px] pl-10`}
            />
          </div>
        </div>
        <div>
          <button
            onClick={openAddModal}
            className="flex shrink-0 items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} />
            Add New Type
          </button>
        </div>
      </div>

      {displayError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {displayError}
        </div>
      )}

      {/* ── Table Card ───────────────────────────────────────────────── */}
      {/* Do NOT add overflow-* here — it breaks the sticky thead. */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">
            {types.length === 0
              ? 'No leave types configured yet.'
              : search
                ? 'No leave types match your search criteria.'
                : 'No leave types available.'}
          </div>
        ) : (
          <table className="w-full min-w-[600px] border-collapse text-sm">
            <thead className="sticky -top-6 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(243_244_246)]">
              <tr className="bg-gray-50">
                {leaveTypeColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={`whitespace-nowrap bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${alignClass(col.align)}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, index) => (
                <tr
                  key={row.id ?? `row-${index}`}
                  className={index % 2 === 1 ? 'bg-surface-50/40' : ''}
                >
                  {leaveTypeColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-5 py-4 ${alignClass(col.align)}`}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        {!loading && filtered.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs text-gray-500">
              Showing <strong className="text-gray-800">{filtered.length}</strong>{' '}
              {filtered.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        )}
      </div>

      {/* ── Add/Edit Modal ────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl min-h-[300px] rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">
                {editingId ? 'Edit Leave Type' : 'Add Leave Type'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {saveError && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 border border-red-200">
                {saveError}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Code *">
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="e.g. AL"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </ModalField>
                <ModalField label="Leave Name *">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Annual"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </ModalField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Paid">
                  <div className="flex items-center gap-4 pt-1">
                    {['Yes', 'No'].map((opt) => (
                      <label
                        key={opt}
                        className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="radio"
                          checked={form.paid === opt}
                          onChange={() => setForm((f) => ({ ...f, paid: opt }))}
                          className="h-4 w-4 accent-primary-600"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </ModalField>
                <ModalField label="Status">
                  <div className="flex items-center gap-4 pt-1">
                    {['Active', 'Inactive'].map((opt) => (
                      <label
                        key={opt}
                        className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="radio"
                          checked={form.status === opt}
                          onChange={() => setForm((f) => ({ ...f, status: opt }))}
                          className="h-4 w-4 accent-primary-600"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </ModalField>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
              <button
                onClick={closeModal}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.code.trim() || !form.name.trim()}
                className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModalField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}